<template>
  <div>
    <div class="configurator-actions">
      <div>
        <div class="configurator-title">
          {{ $t('configurator.extras_title') }}
        </div>
        <div class="configurator-subtitle">
          {{ $t('configurator.extras_subtitle') }}
        </div>
      </div>
      <div class="d-flex">
        <b-button
          variant="outline-secondary"
          class="cfg-btn mr-50"
          @click="$emit('prev')"
        >
          ← {{ $t('configurator.step_configuration') }}
        </b-button>
        <b-button
          variant="primary"
          class="cfg-btn"
          @click="$emit('next')"
        >
          {{ $t('configurator.next') }} → {{ $t('configurator.step_summary') }}
        </b-button>
      </div>
    </div>

    <div class="extras-layout">
      <div>
        <!-- Cuadros de control -->
        <div class="cfg-card">
          <div class="cfg-card-header">
            <span class="cfg-card-title">{{ $t('configurator.control_panels') }}</span>
            <a
              v-if="unassigned.length"
              href="#"
              class="small font-weight-bold"
              @click.prevent="autoAssign"
            >{{ $t('configurator.auto_assign') }}</a>
          </div>
          <div class="p-1">
            <p class="cfg-hint mb-1">
              {{ $t('configurator.box_help') }}
            </p>
            <div
              v-for="(box, index) in boxes"
              :key="box.id"
              class="grupo-block"
            >
              <div class="grupo-header">
                <span class="grupo-badge">CC{{ index + 1 }}</span>
                <span class="grupo-name">{{ box.ref || $t('configurator.select_curtains') }}</span>
                <span class="grupo-count">{{ $tc('configurator.curtain_count', box.curtains.length, { count: box.curtains.length }) }}</span>
                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon"
                  @click="removeBox(box.id)"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </div>
              <div class="cuadro-asignacion">
                <div class="unit-pills mb-75">
                  <span class="unit-pills-label">{{ $t('configurator.curtains') }}:</span>
                  <button
                    v-for="item in boxable"
                    :key="item.key"
                    type="button"
                    class="unit-pill"
                    :class="{ selected: box.curtains.includes(item.key) }"
                    :disabled="!item.calc || isInOtherBox(item.key, box)"
                    :title="!item.calc ? $t('configurator.box_needs_measures') : ''"
                    @click="toggleCurtain(box, item.key)"
                  >
                    {{ item.key }}
                  </button>
                </div>
                <div
                  v-if="box.error"
                  class="budget-warning"
                >
                  {{ box.error }}
                </div>
                <template v-if="box.resolved">
                  <div class="cuadro-selector mb-50">
                    <button
                      v-for="option in box.resolved.options"
                      :key="option.ref"
                      type="button"
                      class="cuadro-btn"
                      :class="{ selected: box.ref === option.ref }"
                      @click="selectRef(box, option)"
                    >
                      {{ option.ref }}
                    </button>
                  </div>
                  <div class="unit-pills">
                    <span class="unit-pills-label">{{ $t('configurator.panel') }}:</span>
                    <button
                      v-for="variant in variantsOf(box)"
                      :key="variant.id"
                      type="button"
                      class="cuadro-btn cuadro-btn-sm"
                      :class="{ selected: box.variantId === variant.id }"
                      @click="updateBox({ id: box.id, changes: { variantId: variant.id } })"
                    >
                      {{ variant.modelo }}
                    </button>
                  </div>
                  <div class="cfg-hint mt-50">
                    {{ $t('configurator.box_motors', { motors: box.resolved.motors, amperios: box.resolved.amperios }) }}
                    <span v-if="box.resolved.requires_sig"> · {{ $t('configurator.sig_required') }}</span>
                  </div>
                </template>
              </div>
            </div>
            <button
              type="button"
              class="add-cuadro-link"
              @click="addBox"
            >
              + {{ $t('configurator.add_box') }}
            </button>
            <div
              v-if="withoutBox.length"
              class="unit-pills no-box-row"
            >
              <span class="unit-pills-label">{{ $t('configurator.no_box') }}:</span>
              <button
                v-for="item in withoutBox"
                :key="item.key"
                type="button"
                class="unit-pill"
                :class="{ selected: isNoBox(item.key) }"
                @click="toggleNoBox(item.key)"
              >
                {{ item.key }}
              </button>
            </div>
          </div>
        </div>

        <!-- Extras -->
        <div class="cfg-card">
          <div class="cfg-card-header">
            <span class="cfg-card-title">{{ $t('configurator.extras') }}</span>
            <span
              v-if="extras.length"
              class="cfg-badge cfg-badge-primary dot"
            >{{ $tc('configurator.selected_count', extras.length, { count: extras.length }) }}</span>
          </div>
          <div
            v-for="category in extraCategories"
            :key="category.ref"
            class="extra-cat"
          >
            <div
              class="extra-cat-header"
              :class="{ open: openCategories.includes(category.ref) }"
              @click="toggleCategory(category.ref)"
            >
              <span class="extra-cat-name">{{ category.name }}</span>
              <span
                v-if="countOfCategory(category)"
                class="cfg-badge cfg-badge-primary"
              >{{ countOfCategory(category) }}</span>
              <feather-icon
                icon="ChevronRightIcon"
                class="extra-cat-chevron"
              />
            </div>
            <div
              v-if="openCategories.includes(category.ref)"
              class="extra-cat-body"
            >
              <div
                v-if="category.options.some(p => p.autoIds)"
                class="extra-auto-info"
              >
                {{ $t('configurator.guides_help') }}
              </div>
              <div
                v-for="product in category.options"
                :key="product.id"
              >
                <div
                  class="extra-subopt"
                  :class="{ selected: selectedOf(product).length || openProducts.includes(product.id) }"
                  @click="toggleProduct(product)"
                >
                  <div class="extra-subopt-box" />
                  <span class="extra-subopt-label">{{ product.label }}</span>
                  <span
                    v-if="productPricePending(product)"
                    class="cfg-badge cfg-badge-orange ml-auto"
                  >{{ $t('configurator.price_pending') }}</span>
                </div>
                <template v-if="selectedOf(product).length || openProducts.includes(product.id)">
                  <div class="extra-curtains">
                    <template v-if="applicable(product).length">
                      <span class="extra-curtains-label">{{ $t('configurator.curtains') }}:</span>
                      <button
                        v-for="item in applicable(product)"
                        :key="item.key"
                        type="button"
                        class="unit-pill"
                        :class="{ selected: isSelected(product, item.key) }"
                        @click="toggle(product, item.key)"
                      >
                        {{ item.key }}
                      </button>
                    </template>
                    <span
                      v-else
                      class="extra-curtains-label"
                    >{{ notApplicableText(product) }}</span>
                  </div>
                  <div
                    v-for="extra in (product.autoIds ? [] : selectedOf(product))"
                    :key="extra.curtain"
                    class="extra-comment"
                  >
                    <span class="extra-comment-key">{{ extra.curtain }}</span>
                    <input
                      type="text"
                      :value="product.rules.ral ? extra.ral : extra.comments"
                      :placeholder="product.rules.ral ? $t('configurator.ral_placeholder') : $t('configurator.comments')"
                      @click.stop
                      @change="updateExtra({ productId: extra.product_id, key: extra.curtain, changes: { [product.rules.ral ? 'ral' : 'comments']: $event.target.value } })"
                    >
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Estado de la configuración -->
      <div class="cfg-card">
        <div class="budget-header">
          <span class="budget-title">{{ $t('configurator.status') }}</span>
        </div>
        <div class="p-50">
          <div
            v-for="item in items"
            :key="item.key"
            class="curtain-item"
          >
            <div class="curtain-item-id">
              {{ item.key }}
            </div>
            <div class="curtain-item-info">
              <div class="curtain-item-name">
                {{ item.ref }}
              </div>
              <div class="curtain-item-size">
                <span
                  v-if="!item.control_box"
                  class="text-muted"
                >{{ $t('configurator.no_box_model') }}</span>
                <span
                  v-else-if="boxOfItem(item.key) && boxOfItem(item.key).ref"
                  class="font-weight-bold text-primary"
                >{{ boxLabel(boxOfItem(item.key)) }}</span>
                <span
                  v-else-if="isNoBox(item.key)"
                  class="text-muted"
                >{{ $t('configurator.no_box') }}</span>
                <span
                  v-else
                  class="text-warning"
                >{{ $t('configurator.unassigned') }}</span>
              </div>
              <div
                v-if="extrasNames(item.key)"
                class="curtain-item-extras"
              >
                {{ extrasNames(item.key) }}
              </div>
            </div>
            <div
              class="curtain-item-dot"
              :class="!item.control_box || isNoBox(item.key) || (boxOfItem(item.key) && boxOfItem(item.key).ref) ? 'ok' : 'warn'"
            />
          </div>
        </div>
        <div
          v-if="unassigned.length"
          class="budget-warnings"
        >
          <div class="budget-warning">
            <feather-icon
              icon="AlertTriangleIcon"
              size="14"
            />
            {{ $t('configurator.without_box', { curtains: unassigned.map(i => i.key).join(', ') }) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { BButton } from 'bootstrap-vue'

export default {
  components: {
    BButton,
  },
  data() {
    return {
      openCategories: [],
      openProducts: [],
    }
  },
  computed: {
    ...mapGetters({
      items: 'configurator/getItems',
      ssbKitPriceMissing: 'configurator/ssbKitPriceMissing',
      boxes: 'configurator/getBoxes',
      extras: 'configurator/getExtras',
      catalog: 'configurator/getCatalog',
      boxOfItem: 'configurator/boxOfItem',
      isNoBox: 'configurator/isNoBox',
      extraAppliesTo: 'configurator/extraAppliesTo',
    }),
    boxable() {
      return this.items.filter(i => i.control_box && !i.sin_tarifa)
    },
    // Cortinas que no están en ningún cuadro: se pueden marcar como "Sin cuadro"
    withoutBox() {
      return this.boxable.filter(i => !this.boxOfItem(i.key))
    },
    unassigned() {
      return this.boxable.filter(i => !this.boxOfItem(i.key) && !this.isNoBox(i.key))
    },
    extraCategories() {
      return this.catalog.extras.map(category => {
        // Guías: una sola opción, la API elige SG-120 o SG-130 según la altura
        const auto = category.products.filter(p => p.rules.auto_by_height)
        const options = category.products.filter(p => !p.rules.auto_by_height).map(p => ({ ...p, label: p.name }))
        if (auto.length) options.unshift({ ...auto[0], label: category.name, autoIds: auto.map(p => p.id) })
        return { ...category, options }
      })
    },
  },
  methods: {
    ...mapMutations({
      addBox: 'configurator/addBox',
      removeBox: 'configurator/removeBox',
      updateBox: 'configurator/updateBox',
      toggleBoxCurtain: 'configurator/toggleBoxCurtain',
      toggleNoBox: 'configurator/toggleNoBox',
      toggleExtra: 'configurator/toggleExtra',
      updateExtra: 'configurator/updateExtra',
    }),
    // El kit de fijación SSB va con el precio de la ficha del cliente
    productPricePending(product) {
      if (product.rules && product.rules.price_by === 'client_ssb_kit') return this.ssbKitPriceMissing
      return product.price_pending
    },
    ...mapActions({
      resolveBox: 'configurator/resolveBox',
      resolveBoxesOf: 'configurator/resolveBoxesOf',
    }),
    isInOtherBox(key, box) {
      const owner = this.boxOfItem(key)
      return !!owner && owner.id !== box.id
    },
    async toggleCurtain(box, key) {
      this.toggleBoxCurtain({ id: box.id, key })
      await this.resolveBox(box.id)
    },
    async autoAssign() {
      const pending = this.unassigned.filter(i => i.calc)
      await Promise.all(pending.map(async item => {
        this.addBox()
        const box = this.boxes[this.boxes.length - 1]
        this.toggleBoxCurtain({ id: box.id, key: item.key })
        await this.resolveBox(box.id)
      }))
    },
    selectRef(box, option) {
      this.updateBox({ id: box.id, changes: { ref: option.ref, variantId: option.variants[0].id } })
    },
    variantsOf(box) {
      const option = box.resolved.options.find(o => o.ref === box.ref)
      return option ? option.variants : []
    },
    toggleProduct(product) {
      // Como en el prototipo: marcar la opción despliega las cortinas; desmarcarla quita el extra de todas
      if (this.selectedOf(product).length) {
        this.selectedOf(product).forEach(e => this.toggle(product, e.curtain))
        this.openProducts = this.openProducts.filter(id => id !== product.id)
        return
      }
      this.openProducts = this.openProducts.includes(product.id)
        ? this.openProducts.filter(id => id !== product.id)
        : [...this.openProducts, product.id]
    },
    boxLabel(box) {
      const option = box.resolved && box.resolved.options.find(o => o.ref === box.ref)
      const variant = option && option.variants.find(v => v.id === box.variantId)
      return variant ? `${box.ref} · ${variant.modelo}` : box.ref
    },
    toggleCategory(ref) {
      this.openCategories = this.openCategories.includes(ref)
        ? this.openCategories.filter(c => c !== ref)
        : [...this.openCategories, ref]
    },
    notApplicableText(product) {
      if (product.rules.requires === 'trim') return this.$t('configurator.paint_requires_trim')
      if (product.rules.requires === 'guides') return this.$t('configurator.paint_requires_guides')
      return this.$t('configurator.extra_not_applicable')
    },
    applicable(product) {
      return this.items.filter(item => this.extraAppliesTo(product, item))
    },
    idsOf(product) {
      return product.autoIds || [product.id]
    },
    isSelected(product, key) {
      return this.extras.some(e => e.curtain === key && this.idsOf(product).includes(e.product_id))
    },
    selectedOf(product) {
      return this.extras.filter(e => this.idsOf(product).includes(e.product_id))
    },
    async toggle(product, key) {
      const selected = this.extras.find(e => e.curtain === key && this.idsOf(product).includes(e.product_id))
      this.toggleExtra({ productId: selected ? selected.product_id : product.id, key })
      if (product.rules.requires_sig) await this.resolveBoxesOf(key)
    },
    countOfCategory(category) {
      return category.options.reduce((total, product) => total + this.selectedOf(product).length, 0)
    },
    extrasNames(key) {
      return this.extraCategories
        .flatMap(c => c.options)
        .filter(p => this.isSelected(p, key))
        .map(p => p.label)
        .join(' · ')
    },
  },
}
</script>

<style lang="scss" scoped>
.extras-layout {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 1.25rem;
  align-items: start;
  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
}
.grupo-block {
  overflow: hidden;
  margin-bottom: 1rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
}
.grupo-header {
  display: flex;
  align-items: center;
  gap: .625rem;
  padding: .5rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  background: #f6f6f6;
}
.grupo-badge {
  padding: .18rem .55rem;
  border-radius: 5px;
  background: #ac1a19;
  font-size: .72rem;
  font-weight: 800;
  color: #fff;
}
.grupo-name {
  flex: 1;
  font-size: .875rem;
  font-weight: 600;
  color: #4d4d4d;
}
.grupo-count {
  font-size: .75rem;
  color: #82868b;
}
.cuadro-asignacion {
  padding: .875rem 1rem;
}
.cuadro-selector {
  display: flex;
  flex-wrap: wrap;
  gap: .4rem;
}
.cuadro-btn-sm {
  padding: .2rem .6rem;
  font-size: .72rem;
}
.no-box-row {
  padding-top: .6rem;
  border-top: 1px solid #f0f0f0;
}
.add-cuadro-link {
  display: block;
  width: 100%;
  padding: .6rem 0;
  border: none;
  background: transparent;
  font-size: .8rem;
  font-weight: 600;
  text-align: left;
  color: #ac1a19;
  cursor: pointer;
}
.extra-cat {
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
}
.extra-cat-header {
  display: flex;
  align-items: center;
  gap: .625rem;
  padding: .875rem 1.125rem;
  cursor: pointer;
  user-select: none;
  transition: background .12s;
  &:hover {
    background: #f6f6f6;
  }
  &.open {
    background: #fffaf5;
    .extra-cat-chevron {
      transform: rotate(90deg);
    }
  }
}
.extra-cat-name {
  flex: 1;
  font-size: .875rem;
  font-weight: 600;
  color: #4d4d4d;
}
.extra-cat-chevron {
  color: #b8c2cc;
  transition: transform .2s;
}
.extra-cat-body {
  padding: .25rem 1.125rem .875rem 2.5rem;
}
.extra-auto-info {
  padding: .5rem .5rem .5rem 0;
  font-size: .8rem;
  line-height: 1.5;
  color: #6e6b7b;
}
.extra-subopt {
  display: flex;
  align-items: center;
  gap: .625rem;
  padding: .45rem .5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background .12s;
  &:hover {
    background: #f6f6f6;
  }
  &.selected {
    .extra-subopt-box {
      border-color: #ac1a19;
      background: #ac1a19;
      &::after {
        content: '✓';
        font-size: .65rem;
        font-weight: 800;
        color: #fff;
      }
    }
    .extra-subopt-label {
      font-weight: 600;
      color: #c96707;
    }
  }
}
.extra-subopt-box {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid #b8c2cc;
  border-radius: 4px;
  transition: all .15s;
}
.extra-subopt-label {
  font-size: .8rem;
  color: #4d4d4d;
}
.extra-curtains {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .35rem;
  margin: .25rem .5rem .625rem 2.625rem;
}
.extra-curtains-label {
  margin-right: .125rem;
  font-size: .7rem;
  color: #82868b;
  white-space: nowrap;
}
.extra-comment {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin: .25rem .5rem .5rem 2.625rem;
  input {
    width: 100%;
    padding: .35rem .6rem;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: .78rem;
    color: #4d4d4d;
    outline: none;
    &:focus {
      border-color: #ac1a19;
    }
  }
}
.extra-comment-key {
  min-width: 34px;
  font-size: .72rem;
  font-weight: 700;
  color: #6e6b7b;
}
.curtain-item-extras {
  margin-top: .15rem;
  font-size: .65rem;
  color: #82868b;
}
</style>
